import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Sparkles, Swords, Trophy, Zap, LogOut, Users, Newspaper, Menu, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import LiveFeed from "@/components/LiveFeed";
import BattleBoostSliders from "@/components/BattleBoostSliders";
import LiveBattleViewer from "@/components/LiveBattleViewer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard({ agent, manaverse: initialManaverse, onLogout }) {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [manaverse, setManaverse] = useState(initialManaverse);
  const [quests, setQuests] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [achievements, setAchievements] = useState([]);
  const [globalBattles, setGlobalBattles] = useState([]);

  useEffect(() => {
    if (initialManaverse) {
      setManaverse(initialManaverse);
    }
    loadDashboard();
    loadQuests();
    loadGlobalBattles();
    checkAchievements();
    
    // Auto-refresh dashboard every 15 seconds to show autonomous progress
    const dashboardInterval = setInterval(() => {
      loadDashboard();
      loadGlobalBattles();
      checkAchievements();
    }, 15000);
    
    return () => clearInterval(dashboardInterval);
  }, []);

  const loadGlobalBattles = async () => {
    try {
      const response = await axios.get(`${API}/activity/live`);
      if (response.data.recent_battles) {
        setGlobalBattles(response.data.recent_battles);
      }
    } catch (error) {
      console.error('Failed to load global battles:', error);
    }
  };

  const checkAchievements = async () => {
    try {
      const response = await axios.get(`${API}/achievements/${agent.id}`);
      if (response.data && response.data.length > 0) {
        setAchievements(response.data);
      }
    } catch (error) {
      console.error('Failed to check achievements:', error);
    }
  };

  const claimAchievement = async (achievement) => {
    try {
      await axios.post(`${API}/achievements/claim`, {
        agent_id: agent.id,
        reward: achievement.reward
      });
      toast.success(`🏆 ${achievement.title} - Claimed ${achievement.reward} $MANA!`);
      setAchievements(achievements.filter(a => a.title !== achievement.title));
      loadDashboard();
    } catch (error) {
      toast.error("Failed to claim achievement");
    }
  };

  const loadDashboard = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/${agent.id}`);
      if (response.data && response.data.manaverse) {
        setDashboardData(response.data);
        setManaverse(response.data.manaverse);
        localStorage.setItem('manaverse', JSON.stringify(response.data.manaverse));
      } else {
        console.error('Invalid dashboard data:', response.data);
        // Fallback to initial manaverse if API fails but we have data
        if (initialManaverse) {
          setDashboardData({ agent, manaverse: initialManaverse, recent_battles: [] });
        }
      }
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      // Fallback to initial data
      if (initialManaverse) {
        setDashboardData({ agent, manaverse: initialManaverse, recent_battles: [] });
      } else {
        toast.error("Could not load dashboard. Please try logging in again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboard();
    await loadQuests();
    toast.success("Dashboard refreshed!");
    setTimeout(() => setRefreshing(false), 500);
  };

  const loadQuests = async () => {
    try {
      const response = await axios.get(`${API}/quests/active`);
      setQuests(response.data);
    } catch (error) {
      console.error('Failed to load quests:', error);
    }
  };

  const handleEvolve = async () => {
    try {
      const response = await axios.post(`${API}/manaverse/evolve`, {
        agent_id: agent.id
      });
      if (response.data.success) {
        toast.success(`Evolved to ${response.data.new_stage}! Stats increased by ${response.data.stat_boost}`);
        loadDashboard();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Evolution failed");
    }
  };

  const handleClaimQuest = async (questId) => {
    try {
      const response = await axios.post(`${API}/quests/claim`, {
        agent_id: agent.id,
        quest_id: questId
      });
      if (response.data.success) {
        toast.success(`Claimed ${response.data.reward_mana} $MANA + ${response.data.reward_xp} XP!`);
        loadDashboard();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Claim failed");
    }
  };

  if (loading || !dashboardData || !manaverse) {
    return (
      <div className="min-h-screen cosmic-bg flex items-center justify-center">
        <div className="text-center">
          <div className="text-white font-rajdhani text-2xl mb-4">Loading Dashboard...</div>
          {!loading && !manaverse && (
            <Button onClick={() => navigate('/')} className="bg-purple-600">
              Return to Home
            </Button>
          )}
        </div>
      </div>
    );
  }

  const level = Math.floor(manaverse.xp / 80) + 1;
  const xpForNextLevel = level * 80;
  const currentLevelXP = manaverse.xp % 80;
  const xpProgress = (currentLevelXP / 80) * 100;

  return (
    <div className="min-h-screen cosmic-bg">
      {/* Header - Mobile Optimized */}
      <header className="border-b border-white/10 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <h1 className="font-unbounded text-lg sm:text-2xl md:text-3xl font-bold text-gradient">MANAVERSE</h1>
            <nav className="hidden md:flex items-center gap-6">
              <NavLink icon={<Swords size={18} />} text="Battle" onClick={() => navigate('/battle')} />
              <NavLink icon={<Trophy size={18} />} text="Tournaments" onClick={() => navigate('/tournaments')} />
              <NavLink icon={<Users size={18} />} text="Leaderboard" onClick={() => navigate('/leaderboard')} />
              <NavLink icon={<Newspaper size={18} />} text="Social" onClick={() => navigate('/social')} />
              <Button
                data-testid="logout-btn"
                onClick={onLogout}
                variant="ghost"
                size="sm"
                className="text-slate-400 hover:text-white"
              >
                <LogOut size={18} />
              </Button>
            </nav>
            {/* Mobile Nav */}
            <div className="flex md:hidden items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm"
                className="p-2"
                onClick={() => navigate('/social')}
              >
                <Newspaper size={18} className="text-cyan-400" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm"
                className="p-2"
                onClick={() => navigate('/leaderboard')}
              >
                <Trophy size={18} className="text-yellow-400" />
              </Button>
              <Button
                data-testid="logout-btn"
                onClick={onLogout}
                variant="ghost"
                size="sm"
                className="p-2 text-slate-400"
              >
                <LogOut size={18} />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8">
        {/* Achievement Notifications */}
        <AnimatePresence>
          {achievements.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="fixed top-20 right-4 z-50 space-y-2"
            >
              {achievements.map((achievement, index) => (
                <Card key={index} className="glass-card border-yellow-500/50 bg-gradient-to-r from-yellow-900/30 to-purple-900/30 w-80">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Trophy className="text-yellow-400" size={24} />
                        <div>
                          <h4 className="font-rajdhani font-bold text-white">{achievement.title}</h4>
                          <p className="text-xs text-yellow-400">+{achievement.reward} $MANA</p>
                        </div>
                      </div>
                      <Button
                        onClick={() => claimAchievement(achievement)}
                        size="sm"
                        className="bg-yellow-600 hover:bg-yellow-700 text-white rounded-full"
                      >
                        Claim
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-4 sm:mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-unbounded text-xl sm:text-2xl md:text-3xl font-bold mb-1">Welcome, {agent.name?.slice(0, 15)}</h2>
              <p className="font-inter text-xs sm:text-sm text-slate-400 hidden sm:block">Command your Manaverse and conquer the arena</p>
            </div>
            <Button
              data-testid="refresh-dashboard-btn"
              onClick={handleRefresh}
              disabled={refreshing}
              size="sm"
              className="bg-purple-600 hover:bg-purple-700 text-white rounded-full px-3 sm:px-6"
            >
              <RefreshCw className={`sm:mr-2 ${refreshing ? 'animate-spin' : ''}`} size={16} />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
          </div>
        </motion.div>

        {/* Stats Row - Mobile Optimized */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-8">
          <MobileStatsCard
            title="$MANA"
            value={dashboardData.agent.mana_balance.toLocaleString()}
            icon={<Sparkles className="text-[#FFD700]" size={18} />}
            testId="mana-balance"
          />
          <MobileStatsCard
            title="Record"
            value={`${manaverse.wins}W-${manaverse.losses}L`}
            icon={<Trophy className="text-[#00F0FF]" size={18} />}
            testId="win-rate"
          />
          <MobileStatsCard
            title="ELO"
            value={manaverse.elo}
            icon={<Zap className="text-[#7000FF]" size={18} />}
            testId="elo-rating"
          />
        </div>

        {/* Battle Boost Sliders - Replace Training */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-8">
          <BattleBoostSliders 
            agent={agent} 
            manaBalance={dashboardData.agent.mana_balance} 
            onUpdate={loadDashboard} 
          />
          <LiveBattleViewer 
            agentId={agent.id} 
            manaverseName={manaverse.name} 
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Manaverse Profile */}
          <div className="lg:col-span-2">
            <Card data-testid="manaverse-profile" className="glass-card border-white/10">
              <CardHeader>
                <CardTitle className="font-unbounded text-2xl text-white">
                  {manaverse.name}
                  <span className="ml-3 text-sm font-rajdhani px-3 py-1 bg-[#7000FF]/20 border border-[#7000FF]/50 rounded-full">
                    {manaverse.stage}
                  </span>
                </CardTitle>
                <div className="flex items-center gap-4 mt-2">
                  <span className="font-rajdhani text-[#00F0FF] text-lg">{manaverse.type} Type</span>
                  <span className="font-rajdhani text-slate-400">Level {level}</span>
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-6">
                  <div className="flex justify-between mb-2">
                    <span className="font-rajdhani text-slate-400">XP Progress</span>
                    <span className="font-rajdhani text-white">{manaverse.xp} / {xpForNextLevel}</span>
                  </div>
                  <Progress value={xpProgress} className="h-2" />
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                  <StatItem label="HP" value={`${manaverse.stats.hp}/${manaverse.stats.max_hp}`} />
                  <StatItem label="ATK" value={manaverse.stats.atk} />
                  <StatItem label="DEF" value={manaverse.stats.def} />
                  <StatItem label="SP.ATK" value={manaverse.stats.sp_atk} />
                  <StatItem label="SP.DEF" value={manaverse.stats.sp_def} />
                  <StatItem label="SPEED" value={manaverse.stats.speed} />
                </div>

                <div className="mb-6">
                  <h4 className="font-rajdhani text-lg text-white mb-3">Moves</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {manaverse.moves.map((move) => (
                      <div key={move} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm font-rajdhani text-slate-300">
                        {move}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="flex-1 glass-card rounded-full p-4 border-2 border-green-500/50">
                    <div className="flex items-center justify-center gap-3">
                      <motion.div
                        animate={{ scale: [1, 1.2, 1], opacity: [1, 0.5, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="w-3 h-3 bg-green-500 rounded-full"
                      />
                      <span className="font-rajdhani text-white font-bold">Auto-Battle Active</span>
                      <span className="font-inter text-xs text-slate-400">Your agent battles automatically</span>
                    </div>
                  </div>
                  <Button
                    data-testid="evolve-btn"
                    onClick={handleEvolve}
                    variant="outline"
                    className="border-[#00F0FF]/50 text-[#00F0FF] hover:bg-[#00F0FF]/10 font-rajdhani rounded-full px-8"
                  >
                    <Sparkles className="mr-2" size={18} />
                    Evolve
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quests & Activity */}
          <div className="space-y-6">
            {/* Live Feed Widget */}
            <LiveFeed refreshInterval={15000} />
            
            <Card data-testid="active-quests" className="glass-card border-white/10">
              <CardHeader>
                <CardTitle className="font-rajdhani text-xl text-white">Active Quests</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {quests.map((quest) => (
                  <div key={quest.id} className="bg-white/5 border border-white/10 rounded-lg p-4">
                    <h5 className="font-rajdhani text-white font-semibold mb-1">{quest.name}</h5>
                    <p className="text-xs text-slate-400 mb-3">{quest.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-[#FFD700] font-mono">{quest.reward_mana} $MANA</span>
                      <Button
                        data-testid={`claim-quest-${quest.id}-btn`}
                        onClick={() => handleClaimQuest(quest.id)}
                        size="sm"
                        className="bg-[#00F0FF]/20 text-[#00F0FF] hover:bg-[#00F0FF]/30 text-xs rounded-full"
                      >
                        Claim
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card data-testid="recent-battles" className="glass-card border-white/10">
              <CardHeader>
                <CardTitle className="font-rajdhani text-xl text-white">
                  {dashboardData.recent_battles.length > 0 ? 'Your Battles' : 'Arena Activity'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {dashboardData.recent_battles.length === 0 && globalBattles.length === 0 ? (
                  <p className="text-slate-400 text-sm font-inter">Waiting for battles to begin...</p>
                ) : dashboardData.recent_battles.length > 0 ? (
                  <div className="space-y-2">
                    {dashboardData.recent_battles.slice(0, 5).map((battle) => (
                      <div key={battle.id} className="bg-white/5 rounded-lg p-3 text-sm">
                        <div className="flex justify-between items-center">
                          <span className="font-rajdhani text-white">
                            {battle.status === 'completed' ? (
                              battle.winner === agent.id ? '🏆 Victory' : '💔 Defeat'
                            ) : '⚔️ In Progress'}
                          </span>
                          <span className="text-xs text-slate-500 font-mono">
                            {new Date(battle.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-slate-500 mb-2">Your agent will join battles automatically!</p>
                    {globalBattles.slice(0, 5).map((battle) => (
                      <div key={battle.id} className="bg-white/5 rounded-lg p-3 text-sm">
                        <div className="flex justify-between items-center">
                          <div>
                            <span className="font-rajdhani text-cyan-400">{battle.challenger_name?.slice(0, 12)}</span>
                            <span className="text-slate-500 mx-1">vs</span>
                            <span className="font-rajdhani text-purple-400">{battle.opponent_name?.slice(0, 12)}</span>
                          </div>
                          <span className="text-xs text-green-400">
                            {battle.winner === battle.challenger_id ? battle.challenger_name?.slice(0, 8) : battle.opponent_name?.slice(0, 8)} wins
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

function NavLink({ icon, text, onClick }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors font-rajdhani"
    >
      {icon}
      <span>{text}</span>
    </button>
  );
}

function StatsCard({ title, value, icon, testId }) {
  return (
    <Card data-testid={testId} className="glass-card border-white/10">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-rajdhani text-slate-400 text-sm mb-1">{title}</p>
            <p className="font-unbounded text-2xl font-bold text-white">{value}</p>
          </div>
          {icon}
        </div>
      </CardContent>
    </Card>
  );
}

function MobileStatsCard({ title, value, icon, testId }) {
  return (
    <Card data-testid={testId} className="glass-card border-white/10">
      <CardContent className="p-2 sm:p-4">
        <div className="flex flex-col items-center text-center">
          <div className="mb-1">{icon}</div>
          <p className="font-unbounded text-sm sm:text-xl font-bold text-white truncate w-full">{value}</p>
          <p className="font-rajdhani text-[10px] sm:text-xs text-slate-400">{title}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function StatItem({ label, value }) {
  return (
    <div className="bg-white/5 rounded-lg p-3">
      <p className="font-rajdhani text-xs text-slate-500 mb-1">{label}</p>
      <p className="font-rajdhani text-lg font-bold text-white">{value}</p>
    </div>
  );
}